import {
  ChecklistScope,
  useGetOnboardingGuideDataQuery,
} from 'generated/graphql';
import { useMemo } from 'react';
import { filterChecklistsForStrategy } from '@client/new/pages/creator/guide/model/checklistFiltering';

export { filterChecklistsForStrategy };

export type StrategyGuideLinkVM = {
  progress: number;
  isLoading: boolean;
  allCompleted: boolean;
  hasActiveStrategy: boolean;
};

export type ChecklistForProgress = {
  scope: ChecklistScope;
  strategyIds: string[];
  tasks: { isChecked: boolean }[];
};

export const calculateProgress = (
  checklists: ChecklistForProgress[],
): number => {
  if (checklists.length === 0) return 0;

  const completedChecklists = checklists.filter((c) =>
    c.tasks.every((t) => t.isChecked),
  ).length;

  return Math.round((completedChecklists / checklists.length) * 100);
};

export function useStrategyGuideLinkViewModel(
  influencerId: number,
): StrategyGuideLinkVM {
  const { data, loading } = useGetOnboardingGuideDataQuery({
    variables: {
      creatorId: String(influencerId),
    },
    skip: !influencerId,
    fetchPolicy: 'cache-and-network',
  });

  const activeStrategyId = useMemo(() => {
    if (!data?.GetStrategies) return null;
    return data.GetStrategies.find((s) => s.isSelected)?.id ?? null;
  }, [data?.GetStrategies]);

  const filteredChecklists = useMemo(() => {
    if (!data?.GetChecklists) return [];
    return filterChecklistsForStrategy(data.GetChecklists, activeStrategyId);
  }, [data?.GetChecklists, activeStrategyId]);

  const progress = useMemo(
    () => calculateProgress(filteredChecklists),
    [filteredChecklists],
  );

  const allCompleted = useMemo(() => {
    if (filteredChecklists.length === 0) return false;

    return filteredChecklists.every((checklist) =>
      checklist.tasks.every((task) => task.isChecked),
    );
  }, [filteredChecklists]);

  const hasActiveStrategy = useMemo(() => {
    return activeStrategyId !== null;
  }, [activeStrategyId]);

  const isLoading = loading && !data;

  return {
    progress,
    isLoading,
    allCompleted,
    hasActiveStrategy,
  };
}
